import React, { useEffect, useState, useRef } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  MessageCircle, QrCode, CheckCircle2, XCircle,
  Loader2, RefreshCw, Wifi, WifiOff, Send,
  Bot, Settings, MessageSquare, Phone,
  Clock, ToggleLeft, ToggleRight, AlertCircle, Zap, GitBranch,
  GripVertical, ChevronDown, ChevronUp, Pencil, CornerUpLeft, Smile, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

// Mensagens simuladas para a animação
const DEMO_MESSAGES = [
  { from: "client", text: "Oi, vocês têm internet fibra na minha rua?" },
  { from: "bot",    text: "Olá! 👋 Sou a Ayla da A.N Telecom! Me manda seu CEP que verifico a cobertura 😊" },
  { from: "client", text: "01310-100" },
  { from: "bot",    text: "✅ Boa notícia! Temos cobertura na sua região!\n\n1️⃣ 350 Mega — R$89,90/mês\n2️⃣ 500 Mega — R$99,90/mês ⭐\n3️⃣ 750 Mega — R$149,90/mês" },
  { from: "client", text: "Quero o plano 2!" },
  { from: "bot",    text: "Ótima escolha! 🎉 Qual é o seu nome completo?" },
  { from: "client", text: "João Silva" },
  { from: "bot",    text: "Olá, João! 😊 Qual é o seu CPF?" },
];

function PhoneAnimation() {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const chatRef = useRef(null);

  useEffect(() => {
    const run = async () => {
      for (let i = 0; i < DEMO_MESSAGES.length; i++) {
        const msg = DEMO_MESSAGES[i];
        if (msg.from === "bot") {
          setTyping(true);
          await new Promise(r => setTimeout(r, 1200));
          setTyping(false);
        }
        await new Promise(r => setTimeout(r, 300));
        setVisibleMessages(prev => [...prev, msg]);
        setCurrentIdx(i + 1);
        await new Promise(r => setTimeout(r, msg.from === "bot" ? 1800 : 1000));
      }
      // Loop
      await new Promise(r => setTimeout(r, 2500));
      setVisibleMessages([]);
      setCurrentIdx(0);
    };
    run();
    const interval = setInterval(run, (DEMO_MESSAGES.length * 3500) + 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleMessages, typing]);

  return (
    <div className="relative flex items-center justify-center h-full">
      {/* Glow de fundo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-64 h-80 bg-green-500 opacity-10 rounded-full blur-3xl" />
      </div>

      {/* Celular — shell externo só para aparência */}
      <div className="relative w-64 h-[520px] bg-zinc-900 rounded-[2.5rem] shadow-2xl border-4 border-zinc-700">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-zinc-900 rounded-b-xl z-20" />

        {/* Tela interna com overflow-hidden próprio e bordas arredondadas menores */}
        <div className="absolute inset-0 rounded-[2rem] overflow-hidden flex flex-col">

        {/* Header WhatsApp */}
        <div className="bg-[#075E54] pt-7 pb-2 px-3 flex items-center gap-2 flex-shrink-0 z-10">
          <div className="w-8 h-8 rounded-full bg-green-300 flex items-center justify-center text-xs font-bold text-green-900">A</div>
          <div>
            <p className="text-white text-xs font-semibold leading-none">Ayla - A.N Telecom</p>
            <p className="text-green-300 text-[10px]">online</p>
          </div>
        </div>

        {/* Chat */}
        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5"
          style={{ background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23128C7E' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\"), #ECE5DD" }}
        >
          {visibleMessages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.from === "client" ? "justify-end" : "justify-start"} animate-fade-in`}
            >
              <div
                className={`max-w-[80%] px-2 py-1.5 rounded-lg text-[10px] leading-tight shadow-sm whitespace-pre-line font-medium ${
                  msg.from === "client"
                    ? "bg-[#c8f0b0] text-zinc-900 rounded-tr-none font-medium"
                    : "bg-white text-zinc-900 rounded-tl-none font-medium"
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-white px-3 py-2 rounded-lg rounded-tl-none shadow-sm flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        {/* Input fake */}
        <div className="bg-[#F0F0F0] px-2 py-1.5 flex items-center gap-1.5 flex-shrink-0">
          <div className="flex-1 bg-white rounded-full h-6 px-2 flex items-center">
            <span className="text-zinc-400 text-[9px]">Mensagem</span>
          </div>
          <div className="w-6 h-6 rounded-full bg-[#25D366] flex items-center justify-center">
            <Send className="w-3 h-3 text-white" />
          </div>
        </div>

        </div>{/* fim tela interna */}
      </div>{/* fim celular */}

    </div>
  );
}

// Papéis que enxergam a Árvore de Atendimento — BKO pra cima. Espelha
// PAPEIS_ARVORE do backend (_require_papel_agencia); se um dia um papel
// novo for liberado lá, precisa liberar aqui também.
const PAPEIS_ARVORE = new Set(["bko", "coordenador", "gerente", "admin_company"]);

// ── Avatar reutilizável: foto de perfil se existir, senão iniciais numa
// cor estável (mesma pessoa sempre cai na mesma cor) ────────────────────
const AVATAR_CORES = ["bg-purple-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-cyan-600", "bg-indigo-500"];
function corPorNome(nome) {
  const s = nome || "?";
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_CORES[Math.abs(hash) % AVATAR_CORES.length];
}
function Avatar({ nome, url, size = 32 }) {
  const iniciais = (nome || "?").trim().split(/\s+/).slice(0, 2).map(p => p[0]).join("").toUpperCase();
  const px = `${size}px`;
  if (url) {
    return (
      <img
        src={url}
        alt={nome || "Avatar"}
        className="rounded-full object-cover flex-shrink-0 border border-black/5"
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <div
      className={`rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${corPorNome(nome)}`}
      style={{ width: px, height: px, fontSize: size * 0.4 }}
    >
      {iniciais}
    </div>
  );
}

const RAMO_STYLE = {
  parceiro: { label: "Parceiro", dot: "bg-blue-500", bubble: "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-900 dark:text-blue-100" },
  sistema:  { label: "Sistema",  dot: "bg-purple-500", bubble: "bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/30 text-purple-900 dark:text-purple-100" },
  bko:      { label: "BKO",      dot: "bg-zinc-500", bubble: "bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-800 dark:text-zinc-100" },
};

function AtendimentoTree() {
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company/pipeline");
        setLeads(data.leads || []);
      } catch (err) {
        toast.error(getErrorMessage(err, "Erro ao carregar pedidos"));
      } finally {
        setLoadingLeads(false);
      }
    })();
  }, []);

  const abrirThread = async (leadId) => {
    setSelectedLeadId(leadId);
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/company/pipeline/${leadId}/whatsapp-thread`);
      setThread(data.thread || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao carregar a árvore de atendimento"));
      setThread([]);
    } finally {
      setLoadingThread(false);
    }
  };

  const leadsFiltrados = leads.filter((l) => {
    if (!busca.trim()) return true;
    const q = busca.trim().toLowerCase();
    return (l.name || "").toLowerCase().includes(q) || String(l.numero || "").includes(q);
  });

  const leadSelecionado = leads.find((l) => l.id === selectedLeadId);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 border-2 border-zinc-200 dark:border-zinc-700 rounded-sm overflow-hidden min-h-[480px] bg-white dark:bg-zinc-900">
      {/* Coluna esquerda: tronco — lista de cards do pipeline */}
      <div className="border-r border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 flex flex-col">
        <div className="p-3 border-b border-zinc-200 dark:border-zinc-700">
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou número..."
            className="w-full text-sm px-3 py-2 rounded-sm border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingLeads ? (
            <div className="p-6 text-center text-zinc-400 dark:text-zinc-500 text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Carregando pedidos...
            </div>
          ) : leadsFiltrados.length === 0 ? (
            <div className="p-6 text-center text-zinc-400 dark:text-zinc-500 text-sm">Nenhum pedido encontrado.</div>
          ) : (
            leadsFiltrados.map((lead) => (
              <button
                key={lead.id}
                onClick={() => abrirThread(lead.id)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-100 dark:border-zinc-700/60 transition-colors ${
                  selectedLeadId === lead.id ? "bg-purple-100 dark:bg-purple-500/20" : "hover:bg-zinc-100 dark:hover:bg-zinc-700/60"
                }`}
              >
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{lead.name || "Sem nome"}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">#{lead.numero} · {lead.col}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Coluna direita: ramos — timeline de mensagens do card selecionado */}
      <div className="flex flex-col">
        {!selectedLeadId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 dark:text-zinc-500 p-8 text-center">
            <GitBranch className="h-10 w-10 mb-3" />
            <p className="text-sm">Selecione um pedido à esquerda pra ver a árvore de atendimento por WhatsApp.</p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
              <p className="font-semibold text-zinc-800 dark:text-zinc-100">{leadSelecionado?.name}</p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Pedido #{leadSelecionado?.numero} · {leadSelecionado?.col}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50 dark:bg-zinc-800/40">
              {loadingThread ? (
                <div className="text-center text-zinc-400 dark:text-zinc-500 text-sm py-8">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Carregando conversas...
                </div>
              ) : thread.length === 0 ? (
                <div className="text-center text-zinc-400 dark:text-zinc-500 text-sm py-8">
                  Nenhuma mensagem de WhatsApp registrada pra esse pedido ainda.
                </div>
              ) : (
                thread.map((msg) => {
                  const estilo = RAMO_STYLE[msg.pessoa_papel] || RAMO_STYLE.bko;
                  return (
                    <div key={msg.id} className="flex items-start gap-2.5">
                      <Avatar nome={msg.pessoa_nome} url={msg.pessoa_avatar_url} size={30} />
                      <div className={`flex-1 border rounded-lg px-3.5 py-2.5 text-sm shadow-sm ${estilo.bubble}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold flex items-center gap-1.5">
                            <span className={`h-1.5 w-1.5 rounded-full ${estilo.dot}`} />
                            {estilo.label} · {msg.pessoa_nome || "—"}
                          </span>
                          <span className="text-[11px] opacity-60 whitespace-nowrap">
                            {new Date(msg.timestamp).toLocaleString("pt-BR")}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Tela de Conversas Internas (tickets abertos pela URA "falar com X") ──
const TICKET_STATUS_LABEL = { aberto: "Aberto", em_atendimento: "Em atendimento", fechado: "Fechado" };
const TICKET_STATUS_COLOR = { aberto: "bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300", em_atendimento: "bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300", fechado: "bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300" };

function ConversasInternas() {
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [resposta, setResposta] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  // Mensagens que já apareceram na tela mas ainda não foram confirmadas
  // pelo servidor (relógio, igual ao WhatsApp). Ficam num estado À PARTE do
  // ticket de propósito: o polling de 7s troca o ticket inteiro, e se elas
  // morassem dentro dele sumiriam no meio do envio.
  const [pendentes, setPendentes] = useState([]); // [{ tmpId, ticketId, message, timestamp, citada }]
  const [citando, setCitando] = useState(null);           // mensagem que está sendo respondida (citação)
  const [emojiAbertoId, setEmojiAbertoId] = useState(null); // mensagem com o seletor de reação aberto

  // Com o scroll interno do chat, abrir uma conversa (ou chegar mensagem
  // nova) precisa descer pro fim, igual WhatsApp. Só reage a MUDANÇA de
  // conversa/quantidade de mensagens — o polling de 7s reenvia o mesmo
  // ticket e não deve puxar a tela de quem está lendo o histórico.
  const mensagensRef = useRef(null);
  useEffect(() => {
    const el = mensagensRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ticket?.id, ticket?.mensagens?.length, pendentes.length]);

  const carregarLista = async (silencioso = false) => {
    if (!silencioso) setLoadingTickets(true);
    try {
      const { data } = await api.get("/company/whatsapp/tickets", { params: filtroStatus ? { status: filtroStatus } : {} });
      setTickets(data.tickets || []);
    } catch (err) {
      if (!silencioso) toast.error(getErrorMessage(err, "Erro ao carregar conversas"));
    } finally {
      if (!silencioso) setLoadingTickets(false);
    }
  };

  useEffect(() => { carregarLista(); /* eslint-disable-next-line */ }, [filtroStatus]);

  const abrirTicket = async (id, silencioso = false) => {
    if (!silencioso) { setSelectedId(id); setLoadingTicket(true); setCitando(null); setEmojiAbertoId(null); }
    try {
      const { data } = await api.get(`/company/whatsapp/tickets/${id}`);
      setTicket(data.ticket);
    } catch (err) {
      if (!silencioso) { toast.error(getErrorMessage(err, "Erro ao abrir a conversa")); setTicket(null); }
    } finally {
      if (!silencioso) setLoadingTicket(false);
    }
  };

  // Polling simples: a cada 7s atualiza a lista de conversas e, se tiver
  // uma aberta na tela, atualiza ela também — tudo "silencioso" (sem
  // spinner piscando) pra não incomodar quem tá digitando uma resposta.
  // Pausa quando a aba não está em foco, pra não gastar requisição à toa
  // numa instância pequena (mesmo cuidado da lição do OOM de hoje).
  useEffect(() => {
    const intervalo = setInterval(() => {
      if (document.hidden) return;
      carregarLista(true);
      if (selectedIdRef.current) abrirTicket(selectedIdRef.current, true);
    }, 7000);
    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus]);

  // Depois de enviar: atualiza a conversa SEM spinner e, no mesmo instante,
  // troca a mensagem "pendente" pela confirmada (dois setState juntos = um
  // render só, sem piscar nem duplicar).
  const atualizarAposEnvio = async (ticketId, tmpId) => {
    try {
      const { data } = await api.get(`/company/whatsapp/tickets/${ticketId}`);
      if (selectedIdRef.current === ticketId) setTicket(data.ticket);
    } catch (e) { /* o próximo polling acerta */ }
    setPendentes((p) => p.filter((x) => x.tmpId !== tmpId));
  };

  const enviarResposta = async () => {
    const texto = resposta.trim();
    if (!texto || !selectedId) return;
    const ticketId = selectedId;
    const citou = citando;
    const tmpId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    // Aparece na hora na bolha (com relógio) e a caixa já libera pra próxima.
    setPendentes((p) => [...p, {
      tmpId, ticketId, message: texto, timestamp: new Date().toISOString(),
      citada: citou ? { wa_id: citou.wa_id, texto: (citou.message || "").slice(0, 300) } : null,
    }]);
    setResposta("");
    setCitando(null);

    try {
      const { data } = await api.post(`/company/whatsapp/tickets/${ticketId}/responder`, {
        message: texto,
        ...(citou?.id ? { citar_msg_id: citou.id } : {}),
      });
      if (citou && data?.citacao_enviada === false) {
        toast.warning("Mensagem enviada, mas o WhatsApp não aceitou a citação — ela saiu sem citar.");
      }
      carregarLista(true); // silencioso: não pisca a lista da esquerda
      await atualizarAposEnvio(ticketId, tmpId);
    } catch (err) {
      setPendentes((p) => p.filter((x) => x.tmpId !== tmpId));
      toast.error(getErrorMessage(err, "Erro ao enviar resposta"));
      // devolve o rascunho (e a citação) pra pessoa poder tentar de novo
      setResposta((atual) => atual || texto);
      setCitando((atual) => atual || citou);
    }
  };

  // Reagir com emoji — clicar no mesmo emoji de novo tira a reação.
  const reagir = async (m, emoji) => {
    setEmojiAbertoId(null);
    const novo = m.reacao_atendente === emoji ? "" : emoji;
    try {
      await api.post(`/company/whatsapp/tickets/${selectedId}/reagir`, { msg_id: m.id, emoji: novo });
      await abrirTicket(selectedId, true); // silencioso: não pisca a tela
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao reagir"));
    }
  };

  const fecharTicket = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/company/whatsapp/tickets/${selectedId}/fechar`);
      toast.success("Conversa encerrada.");
      await abrirTicket(selectedId, true);
      carregarLista(true);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao encerrar"));
    }
  };

  const mensagensExibidas = ticket ? [
    ...(ticket.mensagens || []),
    ...pendentes.filter((p) => p.ticketId === ticket.id).map((p) => ({
      id: p.tmpId, direction: "out", autor_nome: ticket.atendido_por_nome || "Você",
      message: p.message, timestamp: p.timestamp, pendente: true, citada: p.citada,
    })),
  ] : [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 border border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-hidden min-h-[480px] md:h-[calc(100vh-15rem)] md:max-h-[820px] bg-white dark:bg-zinc-900">
      <div className="border-r border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 flex flex-col min-h-0">
        <div className="p-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-2">
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="flex-1 text-xs px-2 py-2 rounded-xl border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 [color-scheme:light] dark:[color-scheme:dark]"
          >
            <option value="">Todas</option>
            <option value="aberto">Abertas</option>
            <option value="em_atendimento">Em atendimento</option>
            <option value="fechado">Fechadas</option>
          </select>
          <button onClick={carregarLista} title="Atualizar" className="p-2 rounded-xl border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto max-h-[40vh] md:max-h-none p-2 space-y-1">
          {loadingTickets ? (
            <div className="p-6 text-center text-zinc-400 dark:text-zinc-500 text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Carregando conversas...
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-6 text-center text-zinc-400 dark:text-zinc-500 text-sm">Nenhuma conversa por aqui ainda.</div>
          ) : (
            tickets.map((t) => (
              <button
                key={t.id}
                onClick={() => abrirTicket(t.id)}
                className={`w-full text-left px-3 py-3 rounded-xl transition-colors flex gap-2.5 ${
                  selectedId === t.id ? "bg-purple-100 dark:bg-purple-500/20" : "hover:bg-zinc-100 dark:hover:bg-zinc-700/60"
                }`}
              >
                <Avatar nome={t.solicitante_nome} url={t.solicitante_avatar_url} size={34} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{t.solicitante_nome}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${TICKET_STATUS_COLOR[t.status] || ""}`}>
                      {TICKET_STATUS_LABEL[t.status] || t.status}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                    {t.solicitante_tipo === "equipe" ? `Equipe · ${t.solicitante_papel}` : t.solicitante_tipo === "parceiro" ? "Parceiro" : "Externo"}
                    {" → "}chamou {t.papel_destino?.replace("_", " ")}
                  </p>
                  {t.ultima_mensagem && (
                    <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate mt-0.5">
                      {t.ultima_mensagem.direction === "out" ? "Você: " : ""}{t.ultima_mensagem.message}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-col min-h-0">
        {!selectedId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 dark:text-zinc-500 p-8 text-center">
            <MessageSquare className="h-10 w-10 mb-3" />
            <p className="text-sm">Selecione uma conversa à esquerda pra ver e responder.</p>
          </div>
        ) : loadingTicket || !ticket ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400 dark:text-zinc-500 text-sm">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando...
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Avatar nome={ticket.solicitante_nome} url={ticket.solicitante_avatar_url} size={36} />
                <div>
                  <p className="font-semibold text-zinc-800 dark:text-zinc-100">{ticket.solicitante_nome}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {ticket.solicitante_tipo === "equipe" ? `Equipe · ${ticket.solicitante_papel}` : ticket.solicitante_tipo === "parceiro" ? "Parceiro autorizado" : "Contato externo"}
                    {" · chamou "}{ticket.papel_destino?.replace("_", " ")}
                    {ticket.atendido_por_nome ? ` · atendido por ${ticket.atendido_por_nome}` : ""}
                  </p>
                </div>
              </div>
              {ticket.status !== "fechado" && (
                <button onClick={fecharTicket} className="text-xs px-3 py-1.5 rounded-xl border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700 flex-shrink-0">
                  Encerrar
                </button>
              )}
            </div>
            <div ref={mensagensRef} className="flex-1 min-h-0 overflow-y-auto max-h-[60vh] md:max-h-none p-4 space-y-3 bg-zinc-50 dark:bg-zinc-800/40">
              {mensagensExibidas.map((m) => {
                const saiu = m.direction === "out";
                const podeInteragir = !!m.wa_id && ticket.status !== "fechado";
                // Quem escreveu a mensagem citada: procura ela na própria conversa (pelo ID do WhatsApp)
                const origCitada = m.citada ? (ticket.mensagens || []).find((x) => x.wa_id === m.citada.wa_id) : null;
                const autorCitada = origCitada ? (origCitada.direction === "out" ? (origCitada.autor_nome || "Você") : ticket.solicitante_nome) : null;
                const chipReacao = "text-sm leading-none bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-full px-1.5 py-0.5 shadow-sm";
                const botaoAcao = "p-1.5 rounded-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-300 hover:text-purple-600 dark:hover:text-purple-300 shadow-sm";
                return (
                  <div key={m.id} className={`group flex items-end gap-2 ${saiu ? "justify-end" : "justify-start"}`}>
                    {!saiu && <Avatar nome={ticket.solicitante_nome} url={ticket.solicitante_avatar_url} size={24} />}
                    <div className="relative max-w-[70%] flex flex-col">
                      <div className={`rounded-2xl ${saiu ? "rounded-br-md" : "rounded-bl-md"} px-3.5 py-2.5 text-sm border shadow-sm ${
                        saiu
                          ? "bg-purple-100 dark:bg-purple-500/20 border-purple-200 dark:border-purple-500/30 text-purple-900 dark:text-purple-100"
                          : "bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100"
                      }`}>
                        {m.citada && (
                          <div className="mb-2 border-l-4 border-purple-400 bg-black/5 dark:bg-white/10 rounded-xl px-2.5 py-1.5">
                            {autorCitada && <p className="text-[11px] font-semibold opacity-80">{autorCitada}</p>}
                            <p className="text-xs opacity-75 truncate">{m.citada.texto || "Mensagem"}</p>
                          </div>
                        )}
                        <p className="whitespace-pre-wrap break-words">{m.message}</p>
                        <p className="text-[10px] opacity-60 mt-1 flex items-center gap-1">
                          {new Date(m.timestamp).toLocaleString("pt-BR")}
                          {m.pendente && <span title="Enviando..." className="inline-flex"><Clock className="h-3 w-3" /></span>}
                        </p>
                      </div>

                      {(m.reacao_contato || m.reacao_atendente) && (
                        <div className={`-mt-2 flex gap-1 px-2 ${saiu ? "justify-end" : "justify-start"}`}>
                          {m.reacao_atendente && <span title="Sua reação" className={chipReacao}>{m.reacao_atendente}</span>}
                          {m.reacao_contato && <span title="Reação do contato" className={chipReacao}>{m.reacao_contato}</span>}
                        </div>
                      )}

                      {podeInteragir && (
                        <div className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity ${saiu ? "right-full mr-1.5" : "left-full ml-1.5"}`}>
                          <button title="Reagir" onClick={() => setEmojiAbertoId(emojiAbertoId === m.id ? null : m.id)} className={botaoAcao}>
                            <Smile className="h-3.5 w-3.5" />
                          </button>
                          <button title="Responder" onClick={() => { setCitando(m); setEmojiAbertoId(null); }} className={botaoAcao}>
                            <CornerUpLeft className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}

                      {emojiAbertoId === m.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setEmojiAbertoId(null)} />
                          <div className={`absolute z-20 top-full mt-1 flex gap-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-full shadow-lg px-2 py-1 ${saiu ? "right-0" : "left-0"}`}>
                            {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((e) => (
                              <button
                                key={e}
                                onClick={() => reagir(m, e)}
                                className={`text-lg leading-none px-1 rounded-full hover:scale-125 transition-transform ${m.reacao_atendente === e ? "bg-purple-100 dark:bg-purple-500/30" : ""}`}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    {saiu && <Avatar nome={ticket.atendido_por_nome || "Você"} url={ticket.atendido_por_avatar_url} size={24} />}
                  </div>
                );
              })}
            </div>
            {ticket.status !== "fechado" ? (
              <div className="border-t border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
                {citando && (
                  <div className="px-3 pt-3">
                    <div className="flex items-start gap-2 bg-purple-50 dark:bg-purple-500/10 border-l-4 border-purple-500 rounded-xl px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-purple-700 dark:text-purple-300">
                          Respondendo a {citando.direction === "out" ? (citando.autor_nome || "você") : ticket.solicitante_nome}
                        </p>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 truncate">{citando.message}</p>
                      </div>
                      <button onClick={() => setCitando(null)} title="Cancelar resposta" className="p-1 text-zinc-500 hover:text-zinc-800 dark:hover:text-white flex-shrink-0">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="p-3 flex items-end gap-2">
                <textarea
                  value={resposta}
                  onChange={(e) => setResposta(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarResposta(); } }}
                  placeholder="Responder..."
                  className="flex-1 text-sm px-4 py-2.5 rounded-2xl border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 resize-none h-16 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <button
                  onClick={enviarResposta}
                  disabled={!resposta.trim()}
                  className="h-11 w-11 rounded-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 flex items-center justify-center flex-shrink-0 transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
                </div>
              </div>
            ) : (
              <div className="p-3 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 text-center text-xs text-zinc-400 dark:text-zinc-500">
                Conversa encerrada.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Catálogo de comandos da URA (Modo Árvore) — espelha URA_COMANDOS_CATALOGO
// do backend (server.py). Só o "id" e "ativo" são salvos/enviados; label,
// emoji e descrição são só pra exibição aqui.
const URA_COMANDOS_CATALOGO = {
  novo_pedido: { emoji: "📋", label: "Abrir pedido novo", descricao: "parceiro autorizado manda o checklist do cliente" },
  consultar_viabilidade: { emoji: "📡", label: "Consultar viabilidade", descricao: "manda só o CEP, checa cobertura Claro/Vivo/TIM" },
  status_pedido: { emoji: "🔎", label: "Status do pedido", descricao: "manda o CPF do cliente, mostra em que etapa tá" },
  falar_bko: { emoji: "🛠️", label: "Falar com BKO", descricao: "chama o backoffice pra atender por aqui" },
  falar_gerencia: { emoji: "🙋", label: "Falar com gerente/coordenador", descricao: "chama a gerência pra atender por aqui" },
};
const URA_COMANDOS_PADRAO = Object.keys(URA_COMANDOS_CATALOGO).map(id => ({ id, ativo: true }));

/**
 * Lista de badges arrastáveis pra configurar a URA: o admin_company arrasta
 * pra reordenar (a ordem final vira a numeração 1️⃣2️⃣3️⃣ do menu no
 * WhatsApp) e clica pra ativar/desativar cada um. Comandos desativados
 * ficam visualmente "apagados" mas continuam na lista (não some — só não
 * entra no menu numerado enviado pro WhatsApp).
 */
const STATUS_PEDIDO_MODO_OPCOES = [
  { id: "perguntar", label: "Perguntar toda vez", descricao: "pergunta \"1-CPF ou 2-Número da venda\" a cada consulta" },
  { id: "cpf", label: "Sempre pelo CPF do cliente", descricao: "mais simples, mas pode juntar pedidos de vendedores diferentes no mesmo CPF" },
  { id: "numero_venda", label: "Sempre pelo Número da venda", descricao: "recomendado quando mais de um vendedor sobe pedido pro mesmo cliente" },
];

function UraComandosConfig({ comandos, onChange, statusPedidoModo, onChangeStatusPedidoModo }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", descricao: "" });

  const lista = comandos && comandos.length ? comandos : URA_COMANDOS_PADRAO;

  const moverPara = (de, para) => {
    if (de === para) return;
    const nova = [...lista];
    const [item] = nova.splice(de, 1);
    nova.splice(para, 0, item);
    onChange(nova);
  };

  const toggleAtivo = (idx) => {
    const nova = lista.map((c, i) => i === idx ? { ...c, ativo: !c.ativo } : c);
    onChange(nova);
  };

  const abrirEdicao = (cmd) => {
    const info = URA_COMANDOS_CATALOGO[cmd.id];
    setEditandoId(cmd.id);
    setEditForm({ label: cmd.label || info.label, descricao: cmd.descricao || info.descricao });
  };

  const salvarEdicao = () => {
    const nova = lista.map(c => c.id === editandoId
      ? { ...c, label: editForm.label.trim(), descricao: editForm.descricao.trim() }
      : c);
    onChange(nova);
    setEditandoId(null);
  };

  const restaurarPadrao = (id) => {
    const nova = lista.map(c => {
      if (c.id !== id) return c;
      const { label, descricao, ...resto } = c;
      return resto;
    });
    onChange(nova);
    setEditandoId(null);
  };

  const ativosCount = lista.filter(c => c.ativo).length;

  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
      <p className="text-xs text-zinc-500 mb-3">
        Arraste pra reordenar — a ordem aqui vira a numeração do menu (1️⃣, 2️⃣...) que a pessoa
        recebe no WhatsApp. Clique num badge pra ativar/desativar (os desativados não aparecem no menu).
        Clique no lápis pra reescrever o texto que a pessoa vê.
      </p>
      {ativosCount === 0 && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-3">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          Nenhuma opção ativa — a URA vai só pedir pra escrever "falar com bko" até você ativar pelo menos uma.
        </div>
      )}
      <div className="space-y-2">
        {lista.map((cmd, idx) => {
          const info = URA_COMANDOS_CATALOGO[cmd.id];
          if (!info) return null;
          const numero = lista.slice(0, idx + 1).filter(c => c.ativo).length;
          const label = cmd.label || info.label;
          const descricao = cmd.descricao || info.descricao;
          const personalizado = Boolean(cmd.label || cmd.descricao);

          if (editandoId === cmd.id) {
            return (
              <div key={cmd.id} className="border border-purple-300 rounded-sm px-3 py-3 bg-white space-y-2">
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span>{info.emoji}</span>
                  <span>Editando opção do menu</span>
                </div>
                <label className="block text-xs text-zinc-500">
                  Texto da opção
                  <input
                    value={editForm.label}
                    onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                    maxLength={60}
                    className="block w-full mt-1 px-2 py-1.5 text-sm border border-zinc-200 rounded-sm"
                  />
                </label>
                <label className="block text-xs text-zinc-500">
                  Descrição (aparece ao lado do texto no menu)
                  <input
                    value={editForm.descricao}
                    onChange={e => setEditForm(f => ({ ...f, descricao: e.target.value }))}
                    maxLength={140}
                    className="block w-full mt-1 px-2 py-1.5 text-sm border border-zinc-200 rounded-sm"
                  />
                </label>
                {cmd.id === "status_pedido" && (
                  <div className="pt-1 border-t border-zinc-100 mt-1">
                    <label className="block text-xs text-zinc-500 mt-2 mb-1">
                      Como verificar o status do pedido
                    </label>
                    <p className="text-[11px] text-zinc-400 mb-2">
                      Se mais de um vendedor sobe pedido pro mesmo cliente, buscar pelo CPF pode
                      trazer resultados de pedidos diferentes misturados. Trave em "Número da
                      venda" pra evitar essa bagunça.
                    </p>
                    <div className="space-y-1.5">
                      {STATUS_PEDIDO_MODO_OPCOES.map(op => (
                        <label
                          key={op.id}
                          className={`flex items-start gap-2 border rounded-sm px-2.5 py-2 cursor-pointer ${
                            (statusPedidoModo || "perguntar") === op.id
                              ? "border-purple-300 bg-purple-50"
                              : "border-zinc-200 bg-white"
                          }`}
                        >
                          <input
                            type="radio"
                            name="status_pedido_modo"
                            className="mt-0.5"
                            checked={(statusPedidoModo || "perguntar") === op.id}
                            onChange={() => onChangeStatusPedidoModo?.(op.id)}
                          />
                          <span>
                            <span className="block text-xs font-medium text-zinc-800">{op.label}</span>
                            <span className="block text-[11px] text-zinc-400">{op.descricao}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <button type="button" onClick={salvarEdicao}
                    className="px-3 py-1.5 text-xs font-semibold rounded-sm bg-purple-600 text-white">
                    Salvar
                  </button>
                  <button type="button" onClick={() => setEditandoId(null)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-sm border border-zinc-200 text-zinc-600">
                    Cancelar
                  </button>
                  {personalizado && (
                    <button type="button" onClick={() => restaurarPadrao(cmd.id)}
                      className="ml-auto text-xs text-zinc-400 underline">
                      Restaurar texto padrão
                    </button>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div
              key={cmd.id}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragIdx !== null) moverPara(dragIdx, idx); setDragIdx(null); }}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-2 border rounded-sm px-3 py-2 bg-white cursor-move transition-opacity ${
                cmd.ativo ? "border-zinc-200" : "border-zinc-100 opacity-40"
              } ${dragIdx === idx ? "ring-2 ring-blue-300" : ""}`}
            >
              <GripVertical className="h-4 w-4 text-zinc-300 flex-shrink-0" />
              <span className="text-xs font-mono text-zinc-400 w-5 flex-shrink-0 text-center">
                {cmd.ativo ? numero : "—"}
              </span>
              <button
                type="button"
                onClick={() => toggleAtivo(idx)}
                className="flex-1 flex items-center gap-2 text-left min-w-0"
              >
                <span className="flex-shrink-0">{info.emoji}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-zinc-800 truncate">{label}{personalizado && <span className="text-purple-500 font-normal"> (editado)</span>}</span>
                  <span className="block text-xs text-zinc-400 truncate">{descricao}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => abrirEdicao(cmd)}
                title="Editar texto dessa opção"
                className="p-1 text-zinc-400 hover:text-purple-600 flex-shrink-0"
              >
                <Pencil className="h-4 w-4" />
              </button>
              {cmd.ativo
                ? <ToggleRight className="h-6 w-6 text-purple-600 flex-shrink-0" onClick={() => toggleAtivo(idx)} />
                : <ToggleLeft className="h-6 w-6 text-zinc-300 flex-shrink-0" onClick={() => toggleAtivo(idx)} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WhatsAppPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("disconnected");
  const [qrCode, setQrCode] = useState(null);
  const [instanceInfo, setInstanceInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [config, setConfig] = useState({
    auto_reply: true, welcome_message: "", business_segment: "", business_instructions: "",
    ura_comandos: URA_COMANDOS_PADRAO,
    status_pedido_modo: "perguntar",
  });
  const [uraDropdownOpen, setUraDropdownOpen] = useState(false);
  const [uraDragIdx, setUraDragIdx] = useState(null);
  const [activeTab, setActiveTab] = useState("connection");
  const [arvoreSubView, setArvoreSubView] = useState("pedidos");
  const [botStats, setBotStats] = useState(null);
  const pollRef = useRef(null);
  const [uploadingBook, setUploadingBook] = useState(false);
  const [bookProgress, setBookProgress] = useState(0);
  const [bookPhase, setBookPhase] = useState("");
  const bookFileRef = useRef(null);

  const planLimits = user?.plan_limits || {};
  const maxBotMessages = planLimits.max_bot_messages ?? 50;
  const canUseWhatsApp = user?.is_admin || maxBotMessages > 0;
  const isFreePlan = !user?.is_admin && (user?.subscription?.plan_key === "free" || !user?.subscription?.plan_key);

  useEffect(() => {
    setStatus("disconnected");
    if (canUseWhatsApp) {
      fetchStatus();
      fetchMessages();
      fetchConfig();
      fetchBotStats();
    }
    return () => clearInterval(pollRef.current);
  }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/whatsapp/status");
      setStatus(data.status || "disconnected");
      setInstanceInfo(data.instance || null);
      if (data.status === "qr") setQrCode(data.qr_code);
      if (data.status === "connected") setQrCode(null);
    } catch {
      setStatus("disconnected");
    }
  };

  const fetchMessages = async () => {
    try {
      const { data } = await api.get("/whatsapp/messages");
      setMessages(data.messages || []);
    } catch {}
  };

  const fetchConfig = async () => {
    try {
      const { data } = await api.get("/whatsapp/config");
      setConfig(data);
    } catch {}
  };

  const handleUploadBook = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "docx"].includes(ext)) {
      toast.error("Só aceita PDF ou Word (.docx).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo muito grande — máximo 8MB.");
      return;
    }

    setUploadingBook(true);
    setBookProgress(0);
    setBookPhase("Enviando arquivo...");

    try {
      // Fase 1: envio do arquivo — progresso real de upload (rápido, arquivo pequeno)
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/whatsapp/upload-book", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            // upload conta como os primeiros 5% — o grosso do tempo é a IA depois
            setBookProgress(Math.round((evt.loaded / evt.total) * 5));
          }
        },
      });

      const jobId = data.job_id;
      setBookPhase("Lendo e resumindo com IA...");

      // Fase 2: consulta o progresso real do job (streaming no backend) a cada 1s
      await new Promise((resolve, reject) => {
        const iv = setInterval(async () => {
          try {
            const { data: job } = await api.get(`/whatsapp/upload-book/status/${jobId}`);
            setBookProgress(job.progress ?? 0);
            if (job.status === "done") {
              clearInterval(iv);
              resolve(job);
            } else if (job.status === "error") {
              clearInterval(iv);
              reject(new Error(job.error || "Erro ao processar o arquivo."));
            }
          } catch (err) {
            clearInterval(iv);
            reject(err);
          }
        }, 1000);
      });

      setBookProgress(100);
      setConfig((c) => ({ ...c, offers_book_filename: file.name }));
      toast.success(`📎 Book "${file.name}" processado! A Ayla já pode usar essas ofertas na conversa.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao processar o arquivo.");
    } finally {
      setUploadingBook(false);
      setBookPhase("");
      setBookProgress(0);
    }
  };

  const handleRemoveBook = async () => {
    try {
      await api.delete("/whatsapp/book");
      setConfig((c) => ({ ...c, offers_book_filename: "" }));
      toast.success("Book removido.");
    } catch { toast.error("Erro ao remover o book."); }
  };

  const fetchBotStats = async () => {
    try {
      const { data } = await api.get("/whatsapp/bot-stats");
      setBotStats(data);
    } catch {}
  };

  const saveConfig = async () => {
    try {
      await api.post("/whatsapp/config", config);
      toast.success("Configurações salvas!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao salvar"));
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/connect-qr-with-setup");
      setStatus("qr");
      setQrCode(data.qr_code);
      toast.info("QR Code gerado! Escaneie com seu WhatsApp.");
      // Sempre limpa qualquer polling anterior antes de criar um novo —
      // evita interval órfão (ex: usuário clicou "Conectar" 2x) que nunca
      // é parado e fica disparando o toast de conexão a cada 3s pra sempre.
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get("/whatsapp/status");
          if (s.status === "connected") {
            clearInterval(pollRef.current);
            setStatus("connected");
            setInstanceInfo(s.instance);
            setQrCode(null);
            fetchBotStats();
            toast.success("WhatsApp conectado! 🎉 Bot ativado automaticamente.");
          } else if (s.status === "qr" && s.qr_code && s.qr_code !== qrCode) {
            setQrCode(s.qr_code);
          }
        } catch {}
      }, 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao conectar"));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Deseja desconectar o WhatsApp?")) return;
    setBusy(true);
    try {
      await api.post("/whatsapp/disconnect");
      clearInterval(pollRef.current);
      setStatus("disconnected");
      setQrCode(null);
      setInstanceInfo(null);
      toast.success("WhatsApp desconectado");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao desconectar"));
    } finally {
      setBusy(false);
    }
  };

  const refreshQr = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/whatsapp/instance/qrcode");
      if (data.qr_code) {
        setQrCode(data.qr_code);
        toast.success("QR Code atualizado!");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao atualizar QR"));
    } finally {
      setBusy(false);
    }
  };

  if (user?.is_team_member && user?.team_role === "vendedor") {
    return (
      <div className="p-6 md:p-8 max-w-lg mx-auto">
        <div className="mb-6">
          <p className="font-mono-label text-green-600 mb-1">Seu WhatsApp</p>
          <h1 className="font-display text-2xl font-bold">Conecte seu número</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Conecte seu WhatsApp pessoal pra receber e responder direto por lá quando um pedido
            seu voltar "Para Correção" — sem precisar abrir o painel.
          </p>
        </div>

        <div className="bg-white border-2 border-zinc-200 rounded-sm p-8 text-center">
          {status === "connected" ? (
            <>
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wifi className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="font-display text-xl font-bold mb-1">Conectado!</h2>
              <p className="text-sm text-zinc-500 mb-6">{instanceInfo?.number || "Seu WhatsApp está ativo."}</p>
              <Button onClick={disconnect} disabled={busy} variant="outline" className="rounded-sm">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <WifiOff className="h-4 w-4 mr-2" />}
                Desconectar
              </Button>
            </>
          ) : status === "qr" && qrCode ? (
            <>
              <img src={qrCode} alt="QR Code" className="mx-auto mb-4 w-56 h-56 border-2 border-zinc-200 rounded-sm" />
              <p className="text-sm text-zinc-500 mb-4">Escaneie com o WhatsApp do seu celular (Aparelhos conectados)</p>
              <Button onClick={refreshQr} disabled={busy} variant="outline" size="sm" className="rounded-sm">
                <RefreshCw className="h-3.5 w-3.5 mr-2" /> Atualizar QR Code
              </Button>
            </>
          ) : (
            <>
              <div className="h-16 w-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="h-8 w-8 text-zinc-400" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">WhatsApp não conectado</h2>
              <p className="text-sm text-zinc-500 mb-6">Conecte pra começar a receber os avisos de pedido.</p>
              <Button onClick={connect} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <QrCode className="h-4 w-4 mr-2" />}
                Conectar WhatsApp
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!canUseWhatsApp) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-white border-2 border-zinc-200 rounded-sm p-10 text-center">
          <div className="h-16 w-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bot className="h-8 w-8 text-zinc-400" />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2">Automação de WhatsApp</h2>
          <p className="text-zinc-500 mb-6">
            Conecte seu WhatsApp gratuitamente e automatize até <strong>50 respostas por mês</strong>.
          </p>
          <Button onClick={() => navigate("/planos")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm">
            <Zap className="h-4 w-4 mr-2" />Ver planos
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <p className="font-mono-label text-green-600 mb-1">Automação</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">WhatsApp</h1>
        <p className="text-zinc-500 mt-1">Conecte seu WhatsApp e automatize atendimentos com IA.</p>
      </div>

      {/* Banner plano grátis */}
      {isFreePlan && botStats && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-amber-800">Plano Grátis: </span>
            <span className="text-amber-700">
              {botStats.used_this_month}/{botStats.max_bot_messages} respostas usadas este mês.
              {botStats.used_this_month >= botStats.max_bot_messages * 0.8 && (
                <> <button onClick={() => navigate("/planos")} className="underline font-semibold ml-1">Faça upgrade para ilimitado.</button></>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Tabs — rola na horizontal em telas estreitas em vez de estourar
          pra fora do container (acontecia com 4 abas + "Árvore de
          Atendimento", que é a label mais larga). */}
      <div className="flex gap-1 mb-6 bg-zinc-200 rounded-sm p-1 w-full sm:w-fit overflow-x-auto">
        {[
          { id: "connection", label: "Conexão", icon: Wifi },
          { id: "messages",   label: "Conversas", icon: MessageSquare },
          { id: "config",     label: "Configurações", icon: Settings },
          ...(PAPEIS_ARVORE.has(user?.team_role) || !user?.is_team_member
            ? [{ id: "arvore", label: "Árvore de Atendimento", icon: GitBranch }]
            : []),
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setActiveTab(id); if (id === "messages") fetchMessages(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${
              activeTab === id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* CONNECTION TAB */}
      {activeTab === "connection" && (
        <div className="relative rounded-2xl overflow-hidden border border-zinc-200 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 min-h-[420px] flex flex-col md:flex-row items-stretch">

          {/* Fundo decorativo */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 right-1/3 w-96 h-96 bg-green-500 opacity-5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-blue-500 opacity-5 rounded-full blur-3xl" />
          </div>

          {/* Lado esquerdo: animação do celular — só em telas médias+, pra
              não estourar a largura em telas de celular (o card de conectar
              já é a informação essencial ali). */}
          <div className="hidden md:flex flex-1 items-center justify-center py-10 px-8 relative">
            <PhoneAnimation />
            {/* Badge esquerdo — fora do celular, lado esquerdo */}
            <div className="absolute left-4 bottom-16 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg px-3 py-2 flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
              <Bot className="w-3.5 h-3.5 text-blue-500" />
              IA ativa 24h
            </div>
            {/* Badge direito — fora do celular, lado direito */}
            <div className="absolute right-4 top-16 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg px-3 py-2 flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Bot respondendo
            </div>
          </div>

          {/* Divider — vertical no desktop, some no mobile (empilhado) */}
          <div className="hidden md:block w-px bg-white/10 my-8" />

          {/* Card de conexão — largura total empilhado no mobile, coluna
              fixa do lado direito no desktop */}
          <div className="w-full md:w-80 flex items-center justify-center p-6 md:p-8">
            <div className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl">

              {status === "disconnected" && (
                <div className="text-center">
                  <div className="h-14 w-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <WifiOff className="h-7 w-7 text-white/60" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-1">Conecte seu WhatsApp</h3>
                  <p className="text-white/50 text-sm mb-5">Escaneie o QR Code e o bot começa a responder automaticamente</p>
                  <Button
                    onClick={connect}
                    disabled={busy}
                    className="w-full bg-[#25D366] hover:bg-[#1da851] text-white rounded-xl h-11 font-semibold shadow-lg"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <QrCode className="h-4 w-4 mr-2" />}
                    Gerar QR Code
                  </Button>
                  <p className="text-white/30 text-xs mt-3">
                    Instância e webhook criados automaticamente
                  </p>
                </div>
              )}

              {status === "qr" && qrCode && (
                <div className="text-center">
                  <p className="text-white font-semibold mb-3">Escaneie com seu WhatsApp</p>
                  <div className="bg-white rounded-xl p-2 inline-block mb-3 shadow-lg">
                    <img src={qrCode} alt="QR Code" className="w-44 h-44" />
                  </div>
                  <p className="text-white/50 text-xs mb-3">
                    WhatsApp → Dispositivos conectados → Conectar aparelho
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-green-400 mb-3">
                    <Loader2 className="h-4 w-4 animate-spin" />Aguardando...
                  </div>
                  <Button variant="outline" onClick={refreshQr} disabled={busy}
                    className="w-full rounded-xl text-xs h-8 border-white/20 text-white/70 hover:bg-white/10">
                    <RefreshCw className="h-3 w-3 mr-1" />Atualizar QR Code
                  </Button>
                </div>
              )}

              {status === "connected" && (
                <div className="text-center">
                  <div className="h-14 w-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-green-500/40">
                    <CheckCircle2 className="h-7 w-7 text-green-400" />
                  </div>
                  <p className="text-green-400 font-semibold text-lg mb-1">Conectado! ✅</p>
                  {instanceInfo?.owner && (
                    <p className="text-white/60 text-sm mb-1">{instanceInfo.owner}</p>
                  )}
                  <p className="text-white/40 text-xs mb-5">Bot ativo — respondendo automaticamente</p>

                  {botStats && (
                    <div className="bg-white/5 rounded-xl p-3 mb-4 text-left">
                      <p className="text-white/40 text-xs mb-1">Respostas este mês</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-end gap-1">
                          <span className="text-white font-bold text-xl">{botStats.used_this_month}</span>
                          <span className="text-white/40 text-sm mb-0.5">/ {botStats.max_bot_messages >= 999999 ? "∞" : botStats.max_bot_messages}</span>
                        </div>
                        {botStats.max_bot_messages < 999999 && (
                          <button
                            onClick={() => navigate("/planos")}
                            className="text-xs font-semibold bg-green-500 hover:bg-green-400 text-white px-2 py-1 rounded-lg transition-colors"
                          >
                            Upgrade ⚡
                          </button>
                        )}
                      </div>
                      {botStats.max_bot_messages < 999999 && (
                        <div className="w-full bg-white/10 rounded-full h-1.5 mt-2">
                          <div
                            className="bg-green-400 h-1.5 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (botStats.used_this_month / botStats.max_bot_messages) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={disconnect}
                    disabled={busy}
                    className="w-full rounded-xl h-9 text-sm bg-white/5 hover:bg-red-500/20 text-red-400 border border-red-500/20"
                  >
                    <XCircle className="h-4 w-4 mr-2" />Desconectar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MESSAGES TAB */}
      {activeTab === "messages" && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-semibold">Conversas recentes</h3>
            <Button variant="outline" onClick={fetchMessages} className="rounded-sm h-8 text-xs">
              <RefreshCw className="h-3 w-3 mr-1" />Atualizar
            </Button>
          </div>
          {messages.length === 0 ? (
            <div className="p-12 text-center text-zinc-400">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma conversa ainda</p>
              <p className="text-xs mt-1">As mensagens aparecerão aqui quando alguém te contatar</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {messages.map((m, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Phone className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{m.contact_name || m.phone}</p>
                        <p className="text-xs text-zinc-400">{m.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-400">
                      <Clock className="h-3 w-3" />
                      {new Date(m.timestamp).toLocaleString("pt-BR")}
                    </div>
                  </div>
                  <div className="ml-10 space-y-1">
                    <div className="bg-zinc-100 rounded-sm px-3 py-2 text-sm">{m.message}</div>
                    {m.response && (
                      <div className="bg-green-50 border border-green-200 rounded-sm px-3 py-2 text-sm text-green-800">
                        <span className="text-xs font-mono-label text-green-600 mr-2">BOT</span>{m.response}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ÁRVORE DE ATENDIMENTO TAB — BKO pra cima */}
      {activeTab === "arvore" && (
        <div>
          <div className="flex gap-1 mb-4 bg-zinc-100 dark:bg-zinc-800 rounded-xl p-1 w-fit">
            {[
              { id: "pedidos", label: "Pedidos" },
              { id: "conversas", label: "Conversas internas" },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setArvoreSubView(id)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  arvoreSubView === id
                    ? "bg-white dark:bg-zinc-700 shadow-sm text-purple-700 dark:text-purple-300"
                    : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {arvoreSubView === "pedidos" ? (
            <>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-3">
                Tronco = pedido do pipeline. Cada ramo mostra quem tocou esse pedido pelo WhatsApp
                (parceiro que abriu, sistema que notificou, etc.) — dá pra acompanhar o ritmo sem
                precisar perguntar pra ninguém.
              </p>
              <AtendimentoTree />
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-3">
                Conversas abertas quando alguém manda <strong>"falar com BKO/coordenador/gerente"</strong> pro
                WhatsApp árvore. Responde daqui mesmo — a pessoa recebe no WhatsApp normal dela, sem
                precisar saber que tem gente atendendo pelo painel.
              </p>
              <ConversasInternas />
            </>
          )}
        </div>
      )}

      {/* CONFIG TAB */}
      {activeTab === "config" && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-6 max-w-xl text-zinc-900 dark:text-zinc-100">
          <h3 className="font-display text-lg font-semibold mb-6">Configurações de Atendimento</h3>
          <div className="space-y-6">
            {(!user?.is_team_member || user?.team_role === "admin_company") && (
              <div className="border border-purple-200 dark:border-purple-500/30 bg-purple-50 dark:bg-purple-500/10 rounded-sm p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-purple-900 dark:text-purple-200">Modo Árvore de Atendimento</p>
                    <p className="text-xs text-purple-700 dark:text-purple-300/80">
                      Esse número vira exclusivo pro ecossistema interno (BKO/parceiros/vendedores) — a Ayla
                      para de responder aqui, e quem mandar algo fora dos comandos conhecidos recebe um menu
                      explicando as opções configuradas abaixo.
                    </p>
                  </div>
                  <button onClick={() => setConfig(c => ({ ...c, modo_arvore: !c.modo_arvore }))} className="text-purple-600 dark:text-purple-400 flex-shrink-0 ml-3">
                    {config.modo_arvore
                      ? <ToggleRight className="h-8 w-8" />
                      : <ToggleLeft className="h-8 w-8 text-zinc-400 dark:text-zinc-500" />}
                  </button>
                </div>

                {/* Esse bloco SÓ existe quando modo_arvore está ligado — de
                    propósito, pra não confundir quem nem usa o Modo Árvore
                    com uma configuração que não tem efeito nenhum enquanto
                    ele estiver desligado. */}
                {config.modo_arvore && (
                  <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-500/30">
                    <button
                      type="button"
                      onClick={() => setUraDropdownOpen(v => !v)}
                      className="flex items-center gap-1.5 text-xs font-medium text-purple-800 dark:text-purple-300 hover:text-purple-900 dark:hover:text-purple-200"
                    >
                      {uraDropdownOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      Configurar opções do menu da URA
                    </button>
                    {uraDropdownOpen && (
                      <div className="mt-2">
                        <UraComandosConfig
                          comandos={config.ura_comandos}
                          onChange={(nova) => setConfig(c => ({ ...c, ura_comandos: nova }))}
                          statusPedidoModo={config.status_pedido_modo}
                          onChangeStatusPedidoModo={(modo) => setConfig(c => ({ ...c, status_pedido_modo: modo }))}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className={`flex items-center justify-between ${config.modo_arvore ? "opacity-40 pointer-events-none" : ""}`}>
              <div>
                <p className="font-medium text-sm">Resposta automática</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Responde mensagens automaticamente com IA</p>
              </div>
              <button onClick={() => setConfig(c => ({ ...c, auto_reply: !c.auto_reply }))} className="text-blue-600 dark:text-blue-400">
                {config.auto_reply
                  ? <ToggleRight className="h-8 w-8" />
                  : <ToggleLeft className="h-8 w-8 text-zinc-400 dark:text-zinc-500" />}
              </button>
            </div>
            <div className={config.modo_arvore ? "opacity-40 pointer-events-none" : ""}>
              <label className="block font-medium text-sm mb-2">Mensagem de boas-vindas</label>
              <textarea
                value={config.welcome_message || ""}
                onChange={e => setConfig(c => ({ ...c, welcome_message: e.target.value }))}
                placeholder="Ex: Olá! Seja bem-vindo! Como posso te ajudar?"
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-sm px-3 py-2 text-sm h-24 resize-none bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">Deixe vazio para usar a resposta automática padrão</p>
            </div>
            <div className={config.modo_arvore ? "opacity-40 pointer-events-none" : ""}>
              <label className="block font-medium text-sm mb-2">Segmento do negócio</label>
              <select
                value={config.business_segment || ""}
                onChange={e => setConfig(c => ({ ...c, business_segment: e.target.value }))}
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-sm px-3 py-2 text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:border-blue-500"
                style={{ colorScheme: "light" }}
              >
                <option value="" style={{ color: "#18181b", background: "#fff" }}>📶 Internet / Telecom (padrão da Ayla)</option>
                <option value="restaurante" style={{ color: "#18181b", background: "#fff" }}>🍔 Restaurante / Alimentação</option>
                <option value="salão de beleza" style={{ color: "#18181b", background: "#fff" }}>💅 Salão / Estética</option>
                <option value="oficina mecânica" style={{ color: "#18181b", background: "#fff" }}>🔧 Mecânica / Automóveis</option>
                <option value="farmácia" style={{ color: "#18181b", background: "#fff" }}>💊 Farmácia / Saúde</option>
                <option value="academia" style={{ color: "#18181b", background: "#fff" }}>💪 Academia / Fitness</option>
                <option value="pet shop" style={{ color: "#18181b", background: "#fff" }}>🐾 Pet Shop / Veterinária</option>
                <option value="imobiliária" style={{ color: "#18181b", background: "#fff" }}>🏢 Imóveis / Apartamentos</option>
                <option value="advocacia" style={{ color: "#18181b", background: "#fff" }}>⚖️ Advocacia / Jurídico</option>
                <option value="escola" style={{ color: "#18181b", background: "#fff" }}>📚 Educação / Cursos</option>
                <option value="tecnologia" style={{ color: "#18181b", background: "#fff" }}>💻 Tecnologia / Informática</option>
                <option value="loja de roupas" style={{ color: "#18181b", background: "#fff" }}>👗 Roupas / Moda</option>
                <option value="supermercado" style={{ color: "#18181b", background: "#fff" }}>🛒 Supermercado</option>
                <option value="clínica médica" style={{ color: "#18181b", background: "#fff" }}>🏥 Clínica Médica</option>
                <option value="hotel" style={{ color: "#18181b", background: "#fff" }}>🏨 Hotel / Hospedagem</option>
                <option value="construção" style={{ color: "#18181b", background: "#fff" }}>🏗️ Construção / Reforma</option>
              </select>
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                Deixe em "Internet / Telecom" para manter a Ayla com o fluxo de planos e cobertura. Escolhendo outro segmento, o bot passa a usar as instruções abaixo.
              </p>

              {/* Painel de operadoras — só aparece com "Internet/Telecom" selecionado
                  (business_segment vazio). Vazio = comportamento antigo intocado. */}
              {!config.business_segment && (
                <div className="mt-3 bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 rounded-sm p-3">
                  <span className="text-xs font-semibold block mb-1 text-zinc-700 dark:text-zinc-200">
                    📶 Operadoras que a Ayla atende
                  </span>
                  <span className="text-[11px] text-zinc-500 dark:text-zinc-400 block mb-2 leading-relaxed">
                    Deixe todas desmarcadas para manter o fluxo padrão da Ayla (Claro/A.N Telecom).
                    Marcando pelo menos uma aqui, a Ayla passa a consultar a cobertura combinada
                    dessas operadoras e usa as instruções do campo abaixo como suas ofertas.
                  </span>

                  <div className="flex gap-2 flex-wrap mb-3">
                    {[
                      { id: "claro", label: "Claro", cor: "#ef4444", logo: "/logo-claro.png", logoW: 46 },
                      { id: "vivo", label: "Vivo", cor: "#a855f7", logo: "/logo-vivo.png", logoW: 46 },
                      { id: "tim", label: "TIM", cor: "#3b82f6", logo: "/logo-tim.png", logoW: 52 },
                    ].map((op) => {
                      const lista = config.operadoras || [];
                      const ativo = lista.includes(op.id);
                      return (
                        <button
                          key={op.id}
                          type="button"
                          onClick={() =>
                            setConfig((c) => {
                              const atual = c.operadoras || [];
                              return {
                                ...c,
                                operadoras: atual.includes(op.id)
                                  ? atual.filter((o) => o !== op.id)
                                  : [...atual, op.id],
                              };
                            })
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 14px",
                            borderRadius: 100,
                            cursor: "pointer",
                            border: `1.5px solid ${ativo ? op.cor : "#a1a1aa"}`,
                            background: ativo ? `${op.cor}22` : "transparent",
                          }}
                        >
                          {ativo && <span style={{ fontSize: 11, color: op.cor, fontWeight: 700 }}>✓</span>}
                          <img
                            src={op.logo}
                            alt={op.label}
                            style={{
                              height: 15,
                              width: op.logoW,
                              objectFit: "contain",
                              filter: ativo ? "none" : "grayscale(1)",
                              opacity: ativo ? 1 : 0.45,
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>

                  {(config.operadoras || []).length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Atendimento para:</span>
                      {[
                        { id: "pj", label: "🏢 Empresas" },
                        { id: "pf", label: "🏠 Pessoa física" },
                      ].map((m) => (
                        <label key={m.id} className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: (config.modo_pessoa || "pj") === m.id ? undefined : "#a1a1aa", fontWeight: (config.modo_pessoa || "pj") === m.id ? 600 : 400 }}>
                          <input
                            type="radio"
                            name="wa-modo-pessoa"
                            checked={(config.modo_pessoa || "pj") === m.id}
                            onChange={() => setConfig((c) => ({ ...c, modo_pessoa: m.id }))}
                          />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block font-medium text-sm mb-2">
                Prompt de atendimento <span className="text-zinc-400 dark:text-zinc-500">(como o bot conversa nesse segmento)</span>
              </label>
              <textarea
                value={config.business_instructions || ""}
                onChange={e => setConfig(c => ({ ...c, business_instructions: e.target.value }))}
                placeholder='Você pode colar aqui o prompt de como deseja que o bot converse — pode até pedir ajuda do ChatGPT ou do Claude pra escrever. Ex: "Você é a Camila, atendente da Clínica Sorriso. Quando o cliente quiser agendar, pergunte o melhor dia e horário, e colete nome e telefone antes de confirmar..."'
                className="w-full border border-zinc-200 dark:border-zinc-700 rounded-sm px-3 py-2 text-sm h-32 resize-none bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                {config.business_segment
                  ? "Recomendado preencher: é esse texto que define como o bot atende, coleta dados e conduz a conversa nesse segmento."
                  : "Opcional no segmento Internet/Telecom — a Ayla já tem o fluxo de planos e cobertura pronto."}
              </p>

              <div className="mt-3">
                <input type="file" ref={bookFileRef} accept=".pdf,.docx" onChange={handleUploadBook} style={{ display: "none" }} />
                {config.offers_book_filename ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-full px-3 py-1">
                      📎 {config.offers_book_filename}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveBook}
                      className="text-xs text-zinc-400 dark:text-zinc-500 hover:text-red-500"
                    >
                      remover
                    </button>
                  </div>
                ) : uploadingBook ? (
                  <div style={{ width: "100%", maxWidth: 320 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11.5, color: "#a1a1aa" }}>{bookPhase}</span>
                      <span style={{ fontSize: 11.5, color: "#a1a1aa", fontWeight: 600 }}>{bookProgress}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 100, background: "#27272a", overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${bookProgress}%`,
                          background: "linear-gradient(90deg, #3b82f6, #60a5fa)",
                          borderRadius: 100,
                          transition: "width .3s ease-out",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => bookFileRef.current?.click()}
                    title="Anexe o book de ofertas/produtos (PDF ou Word) em vez de escrever tudo no prompt — a IA lê e aprende sozinha"
                    className="dark:border-zinc-600 dark:hover:bg-zinc-800"
                    style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "#a1a1aa", background: "transparent", border: "1px solid #3f3f46", borderRadius: 20, padding: "4px 10px", cursor: "pointer" }}
                  >
                    📎 Anexar book de ofertas (opcional)
                  </button>
                )}
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  PDF ou Word, até 8MB. Funciona pra qualquer segmento — cardápio, tabela de preços,
                  catálogo de produtos ou book de planos. A Ayla extrai o texto e passa a usar essas
                  ofertas na conversa, além do que estiver escrito no prompt acima.
                </p>
              </div>
            </div>
            <Button onClick={saveConfig} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm">
              Salvar configurações
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
